import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    currencyUnit,
    dividePercent,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    formatDateLabel,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    OrderStatusSummary,
    Panel,
    projectCurrency,
    ReportingPageDescription,
} from "./ReportingLab.jsx";

function granularDates(data) {
    return [...new Set([
        ...Object.keys(data?.orderAmountGrossGranular ?? {}),
        ...Object.keys(data?.orderAmountNetGranular ?? {}),
        ...Object.keys(data?.orderAmountReceivedGranular ?? {}),
        ...Object.keys(data?.orderAmountCompletedGranular ?? {}),
        ...Object.keys(data?.orderAmountCancelledGranular ?? {}),
        ...Object.keys(data?.orderTurnoverGrossGranular ?? {}),
        ...Object.keys(data?.orderTurnoverNetGranular ?? {}),
        ...Object.keys(data?.orderTurnoverCancelledGranular ?? {}),
    ])].sort();
}

function granularMoneyMajor(granular, date) {
    return moneyToMajor(granular?.[date]);
}

function granularCount(granular, date) {
    return Number(granular?.[date] ?? 0);
}

export default function ReportingOrdersLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setData(null);

        new ErpCalculationApi(getAuthorityConfiguration())
            .shopPerformance({projectId: project.id, erpFilter})
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setData(nextData);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const dates = useMemo(() => granularDates(data), [data]);
    const currency = data?.currency
        ?? data?.orderTurnoverGross?.currency
        ?? data?.orderTurnoverNet?.currency
        ?? projectCurrency(project);

    const received = Number(data?.orderAmountReceived ?? 0);
    const completed = Number(data?.orderAmountCompleted ?? 0);
    const cancelled = Number(data?.orderAmountCancelled ?? 0);
    const marketingOrders = Number(data?.orderAmountGross ?? 0);
    const businessOrders = Number(data?.orderAmountNet ?? 0);
    const cancellationRate = dividePercent(cancelled, received + completed + cancelled);
    const completionRate = dividePercent(completed, received + completed + cancelled);
    const cancelledTurnover = moneyAmount(data?.orderTurnoverCancelled);
    const cancelledAverage = cancelled > 0 ? cancelledTurnover / cancelled : 0;
    const pendingTurnover = Math.max(0, moneyAmount(data?.orderTurnoverGross) - moneyAmount(data?.orderTurnoverNet));
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const statusTrendOption = {
        color: ["#8b5cf6", "#16a34a", "#ef4444"],
        textStyle: chartTextStyle,
        grid: {left: 18, right: 18, top: 26, bottom: 48, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: dates.map(formatDateLabel),
            axisLabel: {fontSize: 12},
        },
        yAxis: {
            type: "value",
            minInterval: 1,
            name: t("reporting_lab.orders_lab.orders_axis", {defaultValue: "Obj."}),
            nameTextStyle: chartTextStyle,
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        series: [
            {
                name: t("reporting_lab.orders.received", {defaultValue: "Přijaté"}),
                type: "bar",
                stack: "orders",
                data: dates.map((date) => granularCount(data?.orderAmountReceivedGranular, date)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.orders.completed", {defaultValue: "Dokončené"}),
                type: "bar",
                stack: "orders",
                data: dates.map((date) => granularCount(data?.orderAmountCompletedGranular, date)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.orders.cancelled", {defaultValue: "Stornované"}),
                type: "bar",
                stack: "orders",
                data: dates.map((date) => granularCount(data?.orderAmountCancelledGranular, date)),
                barMaxWidth: 28,
            },
        ],
    };

    const basisTrendOption = {
        color: ["#2563eb", "#16a34a", "#7c3aed", "#f97316"],
        textStyle: chartTextStyle,
        grid: {left: 18, right: 56, top: 28, bottom: 48, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, [
                (value) => formatChartCurrency(value, currency),
                (value) => formatChartCurrency(value, currency),
                formatChartNumber,
                formatChartNumber,
            ]),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: dates.map(formatDateLabel),
            axisLabel: {fontSize: 12},
        },
        yAxis: [
            {
                type: "value",
                name: currencyUnit(currency),
                nameTextStyle: chartTextStyle,
                axisLabel: {fontSize: 12, formatter: formatChartNumber},
            },
            {
                type: "value",
                name: t("reporting_lab.orders_lab.orders_axis", {defaultValue: "Obj."}),
                nameTextStyle: chartTextStyle,
                axisLabel: {fontSize: 12, formatter: formatChartNumber},
            },
        ],
        series: [
            {
                name: t("reporting_lab.orders_lab.marketing_turnover", {defaultValue: "Obrat marketingové báze"}),
                type: "bar",
                data: dates.map((date) => granularMoneyMajor(data?.orderTurnoverGrossGranular, date)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.orders_lab.business_turnover", {defaultValue: "Obrat obchodní báze"}),
                type: "bar",
                data: dates.map((date) => granularMoneyMajor(data?.orderTurnoverNetGranular, date)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.metric_basis.marketing.label", {defaultValue: "Marketingový výkon"}),
                type: "line",
                yAxisIndex: 1,
                smooth: true,
                data: dates.map((date) => granularCount(data?.orderAmountGrossGranular, date)),
            },
            {
                name: t("reporting_lab.metric_basis.business.label", {defaultValue: "Obchodní výkon"}),
                type: "line",
                yAxisIndex: 1,
                smooth: true,
                data: dates.map((date) => granularCount(data?.orderAmountNetGranular, date)),
            },
        ],
    };

    const rateTrendOption = {
        color: ["#16a34a", "#ef4444"],
        textStyle: chartTextStyle,
        grid: {left: 18, right: 18, top: 24, bottom: 48, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartPercent(value, 1)),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: dates.map(formatDateLabel),
            axisLabel: {fontSize: 12},
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartPercent(value)},
        },
        series: [
            {
                name: t("reporting_lab.orders_lab.completion_rate", {defaultValue: "Míra dokončení"}),
                type: "line",
                smooth: true,
                data: dates.map((date) => {
                    const rowReceived = granularCount(data?.orderAmountReceivedGranular, date);
                    const rowCompleted = granularCount(data?.orderAmountCompletedGranular, date);
                    const rowCancelled = granularCount(data?.orderAmountCancelledGranular, date);
                    return dividePercent(rowCompleted, rowReceived + rowCompleted + rowCancelled) ?? 0;
                }),
            },
            {
                name: t("reporting_lab.orders_lab.cancellation_rate", {defaultValue: "Míra storen"}),
                type: "line",
                smooth: true,
                data: dates.map((date) => {
                    const rowReceived = granularCount(data?.orderAmountReceivedGranular, date);
                    const rowCompleted = granularCount(data?.orderAmountCompletedGranular, date);
                    const rowCancelled = granularCount(data?.orderAmountCancelledGranular, date);
                    return dividePercent(rowCancelled, rowReceived + rowCompleted + rowCancelled) ?? 0;
                }),
            },
        ],
    };

    const dailyRows = dates.map((date) => {
        const rowReceived = granularCount(data?.orderAmountReceivedGranular, date);
        const rowCompleted = granularCount(data?.orderAmountCompletedGranular, date);
        const rowCancelled = granularCount(data?.orderAmountCancelledGranular, date);
        const rowTotal = rowReceived + rowCompleted + rowCancelled;

        return {
            date,
            received: rowReceived,
            completed: rowCompleted,
            cancelled: rowCancelled,
            marketingOrders: granularCount(data?.orderAmountGrossGranular, date),
            businessOrders: granularCount(data?.orderAmountNetGranular, date),
            turnoverMarketing: data?.orderTurnoverGrossGranular?.[date],
            turnoverBusiness: data?.orderTurnoverNetGranular?.[date],
            cancellationRate: dividePercent(rowCancelled, rowTotal),
            turnoverCancelled: data?.orderTurnoverCancelledGranular?.[date],
        };
    }).reverse();

    return <WideSecureLayout
        title={t("reporting_lab.pages.orders", {defaultValue: "Objednávky a storna"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
    >
        <div className="grid grid-cols-12 gap-4">
                <ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.orders.title", {defaultValue: "K čemu slouží objednávky a storna"})}
                >
                    {t("reporting_lab.descriptions.orders.text", {defaultValue: "Vysvětluje rozdíl mezi marketingovou a obchodní bází metrik, ukazuje přijaté, dokončené a stornované objednávky včetně dopadu na obrat."})}
                </ReportingPageDescription>

                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.orders_lab.marketing_basis", {defaultValue: "Marketingová báze"})} value={renderNumber(marketingOrders)} meta={t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Přijaté bez storen"})}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.orders_lab.business_basis", {defaultValue: "Obchodní báze"})} value={renderNumber(businessOrders)} meta={t("reporting_lab.metric_basis.business.short_label", {defaultValue: "Dokončené"})} tone="good"/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.orders_lab.received_total", {defaultValue: "Přijaté celkem"})} value={renderNumber(received)} meta={<OrderStatusSummary completed={completed} renderNumber={renderNumber} t={t}/>}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.orders.cancelled", {defaultValue: "Stornované"})} value={renderNumber(cancelled)} meta={`${t("reporting_lab.orders_lab.cancellation_rate", {defaultValue: "Míra storen"})}: ${renderPercentValue(cancellationRate)}`}/>
                </div>

                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.orders_lab.cancelled_turnover", {defaultValue: "Stornovaný obrat"})} value={renderCurrencyValue({amount: cancelledTurnover, currency}, true)} meta={t("reporting_lab.orders_lab.cancelled_turnover_meta", {defaultValue: "Hodnota objednávek označených jako stornované"})}/>
                </div>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.orders_lab.cancelled_aov", {defaultValue: "AOV stornovaných"})} value={renderCurrencyValue({amount: cancelledAverage, currency}, true)} meta={t("reporting_lab.orders_lab.cancelled_aov_meta", {defaultValue: "Průměrná hodnota stornované objednávky"})}/>
                </div>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.orders_lab.pending_turnover", {defaultValue: "Zatím nedokončený obrat"})} value={renderCurrencyValue({amount: pendingTurnover, currency}, true)} meta={t("reporting_lab.orders_lab.pending_turnover_meta", {defaultValue: "Rozdíl obratu přijatých bez storen a dokončených"})}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.orders_lab.status_trend", {defaultValue: "Stav objednávek v čase"})} className="col-span-12 xl:col-span-6">
                    <EChart option={statusTrendOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.orders_lab.basis_trend", {defaultValue: "Marketingová vs. obchodní báze"})} className="col-span-12 xl:col-span-6">
                    <EChart option={basisTrendOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.orders_lab.rate_trend", {defaultValue: "Dokončení a storna v čase"})} className="col-span-12 xl:col-span-5">
                    <EChart option={rateTrendOption} className="h-80"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.orders_lab.basis_explanation", {defaultValue: "Jak číst báze metrik"})} className="col-span-12 xl:col-span-7">
                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-4 dark:border-violet-900 dark:bg-violet-950/20">
                            <div className="text-sm font-bold text-violet-700 dark:text-violet-200">{t("reporting_lab.metric_basis.marketing.label", {defaultValue: "Marketingový výkon"})}</div>
                            <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t("reporting_lab.metric_basis.marketing.description", {defaultValue: "Objednávky, které přišly a nejsou stornované. Vhodné pro vyhodnocení marketingu."})}</div>
                            <div className="mt-3 text-2xl font-bold text-slate-900 dark:text-slate-100">{renderCurrencyValue(data?.orderTurnoverGross, true)}</div>
                        </div>
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 dark:border-emerald-900 dark:bg-emerald-950/20">
                            <div className="text-sm font-bold text-emerald-700 dark:text-emerald-200">{t("reporting_lab.metric_basis.business.label", {defaultValue: "Obchodní výkon"})}</div>
                            <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t("reporting_lab.metric_basis.business.description", {defaultValue: "Jen dokončené objednávky. Vhodné pro majitele, finance a reálný vyfakturovaný výkon."})}</div>
                            <div className="mt-3 text-2xl font-bold text-slate-900 dark:text-slate-100">{renderCurrencyValue(data?.orderTurnoverNet, true)}</div>
                        </div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                        <KpiCard loading={loading} label={t("reporting_lab.orders_lab.completion_rate", {defaultValue: "Míra dokončení"})} value={renderPercentValue(completionRate)} meta={t("reporting_lab.orders.completed", {defaultValue: "Dokončené"})}/>
                        <KpiCard loading={loading} label={t("reporting_lab.orders_lab.cancellation_rate", {defaultValue: "Míra storen"})} value={renderPercentValue(cancellationRate)} meta={t("reporting_lab.orders.cancelled", {defaultValue: "Stornované"})}/>
                        <KpiCard loading={loading} label={t("reporting_lab.table.aov", {defaultValue: "AOV"})} value={renderCurrencyValue(data?.orderAverageGross, true)} meta={t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Přijaté bez storen"})}/>
                        <KpiCard loading={loading} label={t("reporting_lab.orders_lab.business_aov", {defaultValue: "AOV dokončených"})} value={renderCurrencyValue(data?.orderAverageNet, true)} meta={currencyUnit(currency)}/>
                    </div>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.orders_lab.daily_table", {defaultValue: "Denní přehled objednávek"})} className="col-span-12">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.table.date", {defaultValue: "Datum"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders.received", {defaultValue: "Přijaté"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders.completed", {defaultValue: "Dokončené"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders.cancelled", {defaultValue: "Stornované"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders_lab.marketing_basis", {defaultValue: "Marketingová báze"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders_lab.business_basis", {defaultValue: "Obchodní báze"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders_lab.marketing_turnover_short", {defaultValue: "Obrat MKT"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders_lab.business_turnover_short", {defaultValue: "Obrat obchod"})}</th>
                                <th className="py-2 text-right font-semibold">{t("reporting_lab.orders_lab.cancellation_rate", {defaultValue: "Míra storen"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {dailyRows.map((row) => <tr key={row.date}>
                                <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{formatDateLabel(row.date)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.received)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.completed)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.cancelled)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.marketingOrders)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.businessOrders)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.turnoverMarketing, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.turnoverBusiness, true)}</td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.cancellationRate)}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>
    </WideSecureLayout>;
}
